import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Segment,
  Header,
  Button,
  Icon,
  Image,
  Table,
  Label,
  Loader,
  Message,
  Confirm
} from 'semantic-ui-react';
import {
  fetchLitGroupsDirect,
  fetchBooksByLitGroup,
  fetchBookData,
  selectDirectLitGroups,
  selectDirectBooks,
  selectDirectBooksStatus,
  selectBookData
} from '../slices/userLitGroupAssetsSlice';
import useIsLoggedIn from '../isLoggedIn';
import './BookCreatorStateManager.css';
import myLibrary from '../assets/myLibrary.svg'
import bookStack from '../assets/justBookStack.svg'
import importStory from '../assets/importStory.svg'

/**
 * BookCreatorStateManager Component
 * Manages the state for adding new books in the Book Creator process
 * Features three columns: Literary Groups, Books, and PDF Files
 */
const BookCreatorStateManager = ({
  onLitGroupSelect,
  onBookSelect,
  onPdfSelect,
  onAddGroupClick,
  onAddBookClick,
  onImportClick,
  onStateChange,
  onOpenPanel, // New prop for panel control
  onDeletePdf // New prop for deleting PDFs
}) => {
  const dispatch = useDispatch();
  const authToken = useIsLoggedIn();

  // Redux state with proper memoization to prevent unnecessary re-renders
  const rawDirectLitGroups = useSelector(selectDirectLitGroups);
  const directLitGroups = useMemo(() => rawDirectLitGroups || [], [rawDirectLitGroups]);

  const rawBooks = useSelector(selectDirectBooks);
  const books = useMemo(() => rawBooks || [], [rawBooks]);

  const booksStatus = useSelector(selectDirectBooksStatus);
  const bookData = useSelector(selectBookData);

  // Local state
  const [selectedLitGroup, setSelectedLitGroup] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [processedVersions, setProcessedVersions] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pdfToDelete, setPdfToDelete] = useState(null);

  // Get literary groups directly
  const getLitGroups = () => {
    return directLitGroups;
  };

  // Parse literary group name and type (same as LiteraryGroupBookList)
  const getLitGroupName = (name) => {
    if (!name) return '';
    const parts = name.split(' - ');
    return parts[0];
  };

  const getLitGroupType = (name) => {
    if (!name) return '';
    const parts = name.split(' - ');
    return parts.length > 1 ? parts[1] : '';
  };

  // Fetch literary groups on mount
  useEffect(() => {
    if (authToken) {
      dispatch(fetchLitGroupsDirect())
        .catch((error) => {
          console.error('[BookCreatorStateManager] fetchLitGroupsDirect ERROR:', error);
        });
    }
  }, [dispatch, authToken]);

  // Fetch books when literary group is selected
  useEffect(() => {
    if (selectedLitGroup) {
      dispatch(fetchBooksByLitGroup(selectedLitGroup.uuid));
    }
  }, [dispatch, selectedLitGroup]);

  // Fetch book data (including processed versions) when book is selected
  useEffect(() => {
    if (selectedBook) {
      dispatch(fetchBookData({ bookuuid: selectedBook.uuid }));
    }
  }, [dispatch, selectedBook]);

  // Extract processed versions from book data
  useEffect(() => {
    if (bookData?.bookTree?.HAS_PROCESSED_ITEMS) {
      const versions = bookData.bookTree.HAS_PROCESSED_ITEMS[0]?.HAS_PROCESSED_VERSION || [];
      setProcessedVersions(versions);
    } else {
      setProcessedVersions([]);
    }
  }, [bookData]);

  // Emit state changes - only when actual selections change
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        litGroup: selectedLitGroup,
        book: selectedBook,
        pdf: selectedPdf,
        processedVersions
      });
    }
    // Note: onStateChange is intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLitGroup, selectedBook, selectedPdf, processedVersions]);

  // Handlers
  const handleLitGroupSelect = useCallback((litGroup) => {
    setSelectedLitGroup(litGroup);
    setSelectedBook(null);
    setSelectedPdf(null);

    if (onLitGroupSelect) {
      onLitGroupSelect(litGroup);
    }
  }, [onLitGroupSelect]);

  const handleBookSelect = useCallback((book) => {
    setSelectedBook(book);
    setSelectedPdf(null);

    if (onBookSelect) {
      onBookSelect(book);
    }
  }, [onBookSelect]);

  const handlePdfSelect = useCallback((pdf) => {
    setSelectedPdf(pdf);

    if (onPdfSelect) {
      onPdfSelect(pdf);
    }
  }, [onPdfSelect]);

  const handleAddGroup = useCallback(() => {
    // Use new panel system if available, otherwise fallback to old navigation
    if (onOpenPanel) {
      onOpenPanel('add-group');
    } else if (onAddGroupClick) {
      onAddGroupClick();
    }
  }, [onOpenPanel, onAddGroupClick]);

  const handleAddBook = useCallback(() => {
    // Use new panel system if available, otherwise fallback to old navigation
    if (onOpenPanel) {
      onOpenPanel('add-book', { litGroup: selectedLitGroup });
    } else if (onAddBookClick) {
      onAddBookClick(selectedLitGroup);
    }
  }, [onOpenPanel, onAddBookClick, selectedLitGroup]);

  const handleImport = useCallback(() => {
    // Use new panel system if available, otherwise fallback to old navigation
    if (onOpenPanel) {
      onOpenPanel('import', {
        litGroup: selectedLitGroup,
        book: selectedBook,
        pdf: selectedPdf
      });
    } else if (onImportClick) {
      onImportClick({
        litGroup: selectedLitGroup,
        book: selectedBook,
        pdf: selectedPdf
      });
    }
  }, [onOpenPanel, onImportClick, selectedLitGroup, selectedBook, selectedPdf]);

  // Handle PDF deletion
  const handleDeletePdf = useCallback((pdf, event) => {
    // Stop event propagation to prevent selecting the PDF
    event.stopPropagation();
    setPdfToDelete(pdf);
    setDeleteConfirmOpen(true);
  }, []);

  const confirmDeletePdf = useCallback(() => {
    if (pdfToDelete && onDeletePdf) {
      onDeletePdf(pdfToDelete, selectedBook);

      // If the deleted PDF was selected, clear selection
      if (selectedPdf?.uuid === pdfToDelete.uuid) {
        setSelectedPdf(null);
      }
    }
    setDeleteConfirmOpen(false);
    setPdfToDelete(null);

    // Refresh book data to get updated PDF list
    if (selectedBook) {
      dispatch(fetchBookData({ bookuuid: selectedBook.uuid }));
    }
  }, [pdfToDelete, onDeletePdf, selectedBook, selectedPdf, dispatch]);

  const cancelDeletePdf = useCallback(() => {
    setDeleteConfirmOpen(false);
    setPdfToDelete(null);
  }, []);

  return (
    <Segment className="book-creator-state-manager">
      {/* Delete Confirmation Dialog */}
      <Confirm
        open={deleteConfirmOpen}
        header="Delete PDF Import"
        content={`Are you sure you want to delete ${pdfToDelete?.fileName || 'this PDF'}? This action cannot be undone.`}
        onCancel={cancelDeletePdf}
        onConfirm={confirmDeletePdf}
        cancelButton="Cancel"
        confirmButton="Delete"
        size="small"
      />

      <Grid columns={3} divided>
        {/* Column 1: Literary Groups */}
        <Grid.Column>
          <Header as="h3">
            <Image src={myLibrary} />
            <span className='book-creator-state-manager-list-header'>Literary Groups</span>
            <Button
              fluid
              secondary
              icon="plus"
              content="Add Group"
              onClick={handleAddGroup}
              className="action-button"
            />
          </Header>

          <div className="column-content">
            {(() => {
              const groups = getLitGroups();

              if (groups.length === 0) {
                return (
                  <Message info>
                    <p>No literary groups found. Click "Add Group" to create one.</p>
                  </Message>
                );
              } else {
                return (
                  <div className="scrollable-list">
                    <Table basic="very" selectable>
                      <Table.Body>
                        {groups.map((group) => {
                          const groupName = getLitGroupName(group.name);
                          const groupType = getLitGroupType(group.name);

                          return (
                            <Table.Row
                              key={group.uuid}
                              active={selectedLitGroup?.uuid === group.uuid}
                              onClick={() => handleLitGroupSelect(group)}
                              className="list-item"
                            >
                              <Table.Cell>
                                <Icon className='book-creator-state-manager-list-icon' name="folder" />

                                {groupName}
                                {groupType && (
                                  <Label size="tiny" basic>
                                    {groupType}
                                  </Label>
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>
                );
              }
            })()}


          </div>
        </Grid.Column>

        {/* Column 2: Books */}
        <Grid.Column>
          <Header as="h3">
            <Image src={bookStack} />
            <span className='book-creator-state-manager-list-header'>Books</span>
            <Button
              fluid
              secondary
              icon="plus"
              content="Add Book"
              onClick={handleAddBook}
              disabled={!selectedLitGroup}
              className="action-button"
            />
          </Header>

          <div className="column-content">
            {!selectedLitGroup ? (
              <Message warning>
                <p>Select a Literary Group to view its books.</p>
              </Message>
            ) : books.length === 0 ? (
              <Message info>
                <p>No books in this group. Click "Add Book" to create one.</p>
              </Message>
            ) : (
              <div className="scrollable-list">
                <Table basic="very" selectable>
                  <Table.Body>
                    {books.map((book) => (
                      <Table.Row
                        key={book.uuid}
                        active={selectedBook?.uuid === book.uuid}
                        onClick={() => handleBookSelect(book)}
                        className="list-item"
                      >
                        <Table.Cell>
                          <Icon className='book-creator-state-manager-list-icon' name="book" />
                          {book.name}
                          {book.status && (
                            <Label
                              size="tiny"
                              basic
                              color={book.status === 'active' ? 'green' : 'grey'}
                            >
                              {book.status}
                            </Label>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>
        </Grid.Column>

        {/* Column 3: PDF Files */}
        <Grid.Column>
          <Header as="h3">
            <Image src={importStory} />
            <span className='book-creator-state-manager-list-header'>PDF File Imports</span>
            <Button
              fluid
              secondary
              icon="upload"
              content="Import"
              onClick={handleImport}
              disabled={!selectedLitGroup || !selectedBook}
              className="action-button"
            />
          </Header>

          <div className="column-content">
            {!selectedBook ? (
              <Message warning>
                <p>Select a Book to view its PDF files.</p>
              </Message>
            ) : processedVersions.length === 0 ? (
              <Message info>
                <p>No PDF files imported yet. Click "Import" to add one.</p>
              </Message>
            ) : (
              <div className="scrollable-list">
                <Table basic="very" selectable>
                  <Table.Body>
                    {processedVersions.map((pdf) => (
                      <Table.Row
                        key={pdf.uuid}
                        active={selectedPdf?.uuid === pdf.uuid}
                        onClick={() => handlePdfSelect(pdf)}
                        className="list-item pdf-list-item"
                      >
                        <Table.Cell>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ flex: 1 }}>
                              <Icon className='book-creator-state-manager-list-icon' name="file pdf outline" />
                              {pdf.fileName || `Version ${pdf.version || 1}`}
                              {pdf.isActive && (
                                <Label color="green" size="tiny">
                                  Active
                                </Label>
                              )}
                              {pdf.createdOn && (
                                <div className="pdf-meta">
                                  <small>{new Date(pdf.createdOn).toLocaleDateString()}</small>
                                  {pdf.version && (
                                    <small>{`   :     Version ${pdf.version}`}</small>
                                  )}
                                </div>
                              )}
                            </div>
                            <Button
                              icon="trash"
                              className='bw-trash-icon'
                              size="small"
                              onClick={(e) => handleDeletePdf(pdf, e)}
                              style={{ marginLeft: '100px', backgroundColor: "transparent" }}
                              title="Delete this PDF import"
                            />
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}


          </div>
        </Grid.Column>
      </Grid>
    </Segment>
  );
};

export default BookCreatorStateManager;
